import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Input,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import * as fileSaver from 'file-saver'
import { useMutation } from '@tanstack/react-query'

import { useDCAReport } from '../DCAReportProvider'
import {
  getReservesReport,
  saveReservesVersion,
  listReservesVersions,
  getReservesVersion,
  transitionReservesVersion,
  exportReservesReport,
  reconcileReserves,
} from 'service/dcaAnalysis'
import FormSection from 'components/form-section'

// WS6 RS-B — Reserves report section for /decline_analysis/reporting.
//
// Consumes POST /api/report/dca/reserves/ (RS-A1). Everything here renders
// *defensively*: the RS-A3 follow-up will append fields (SEC disclosure notes,
// richer warnings, cum-to-date). Unknown-but-present fields are surfaced;
// missing fields degrade to em-dash without throwing. permian is sparse, so the
// "no wells / all skipped" path is a first-class state, not an error.

const { Text, Title, Paragraph } = Typography

// BOE = 6 Mscf / bbl (matches the DCA panel convention).
const MSCF_PER_BOE = 6

// PUD wells need a Tier-3 type curve (RS-A: pud_requires_type_curve). Until
// that lands we show a placeholder rather than an empty EUR cell.
const PUD_PLACEHOLDER = 'type-curve pending (Tier3)'

const STATUS_COLORS = {
  PDP: 'green',
  PDNP: 'gold',
  PUD: 'blue',
}

// Defensive number formatter: returns em-dash for null/undefined/NaN.
function fmt(value, decimals = 2) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—'
  const n = Number(value)
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

// A phase-aware unit label. BE sends unit ("bbl"|"Mscf"); we present the
// reserves-scale label (Mbbl / MMcf) the DCA panels use.
function scaledUnit(unit) {
  if (unit === 'bbl') return 'Mbbl'
  if (unit === 'Mscf') return 'MMcf'
  return unit || ''
}

// Best-effort BOE from an eur bucket. Only computed for gas (Mscf) so we don't
// double-count oil; oil is already in bbl. Returns null when not derivable.
function toBoe(value, unit) {
  if (value == null || Number.isNaN(Number(value))) return null
  if (unit === 'Mscf') return Number(value) * 1000 / (MSCF_PER_BOE * 1000) // Mscf→BOE
  if (unit === 'bbl') return Number(value)
  return null
}

// Pull a classification bucket regardless of naming scheme. RS-A1 sends either
// reserves_1P/2P/3P or proved/probable/possible — support both.
function pickClass(classification, key) {
  if (!classification) return null
  const aliases = {
    p1: ['reserves_1P', 'proved', '1P'],
    p2: ['reserves_2P', 'probable', '2P'],
    p3: ['reserves_3P', 'possible', '3P'],
  }[key]
  for (const a of aliases) {
    if (classification[a] != null) return classification[a]
  }
  return null
}

function grossNet(bucket) {
  if (!bucket) return { gross: null, net: null }
  return { gross: bucket.gross, net: bucket.net }
}

// --- WS6 RS-D: version lifecycle -------------------------------------------

// Status → tag color. BE may send lower/upper/mixed case; normalize.
const VERSION_STATUS_COLORS = {
  draft: 'default',
  submitted: 'processing',
  approved: 'success',
  rejected: 'error',
  locked: 'gold',
}

function normStatus(status) {
  return String(status || '').toLowerCase()
}

// Which transitions are offered for a given status. Defensive: unknown status
// → no actions (read-only). locked → terminal.
function actionsForStatus(status) {
  switch (normStatus(status)) {
    case 'draft':
      return ['submit']
    case 'submitted':
      return ['approve', 'reject']
    case 'approved':
      return ['lock']
    case 'rejected':
      return ['submit']
    default:
      return []
  }
}

const ACTION_LABELS = {
  submit: 'Submit',
  approve: 'Approve',
  reject: 'Reject',
  lock: 'Lock',
}

const ACTION_BUTTON_TYPE = {
  submit: 'primary',
  approve: 'primary',
  reject: 'default',
  lock: 'default',
}

function isLocked(version) {
  return normStatus(version?.status) === 'locked'
}

// Pull a display value across a few plausible field names.
function firstDefined(obj, keys) {
  if (!obj) return null
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== '') return obj[k]
  }
  return null
}

// --- WS6 RS-C1: reconcile ---------------------------------------------------

// Ordered movement buckets (period-begin → end). Defensive: RS-C1 may rename;
// we look up by a small alias list and label defensively.
const RECON_BUCKETS = [
  { key: 'beginning', label: 'Beginning', aliases: ['beginning', 'begin'] },
  { key: 'revisions', label: 'Revisions', aliases: ['revisions', 'revision'] },
  {
    key: 'extensions_discoveries',
    label: 'Extensions & Discoveries',
    aliases: ['extensions_discoveries', 'extensions', 'discoveries'],
  },
  { key: 'production', label: 'Production', aliases: ['production', 'produced'] },
  {
    key: 'acquisitions',
    label: 'Acquisitions',
    aliases: ['acquisitions', 'acquisition'],
  },
  {
    key: 'divestitures',
    label: 'Divestitures',
    aliases: ['divestitures', 'divestiture', 'dispositions'],
  },
  { key: 'ending', label: 'Ending', aliases: ['ending', 'end'] },
]

function reconValue(bucketObj, aliases) {
  if (!bucketObj) return null
  for (const a of aliases) {
    if (bucketObj[a] != null) return bucketObj[a]
  }
  return null
}

const ReservesReport = () => {
  const { sourceProject } = useDCAReport()
  const { wells = [], id: projectIdFromSource } = sourceProject || {}

  const [complianceBasis, setComplianceBasis] = useState('PRMS')
  const [effectiveDate, setEffectiveDate] = useState(null)
  const [markFinal, setMarkFinal] = useState(false)

  // Evaluator certification block — persisted via saveReservesVersion (RS-D).
  const [evaluator, setEvaluator] = useState({
    name: '',
    qualification: '',
    asOf: '',
    methodStatement: '',
  })
  const [changeNote, setChangeNote] = useState('')

  // Version bar / reconcile local state.
  const [versions, setVersions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [reconPrior, setReconPrior] = useState(null)
  const [reconCurrent, setReconCurrent] = useState(null)
  const [reconResult, setReconResult] = useState(null)

  const {
    mutate: compute,
    data: response,
    isPending,
    reset: resetResult,
  } = useMutation({
    mutationFn: (payload) => getReservesReport(payload),
  })

  // effective_date is required when basis=SEC or the report is marked final.
  const effectiveDateRequired = complianceBasis === 'SEC' || markFinal

  const onGenerate = useCallback(() => {
    if (effectiveDateRequired && !effectiveDate) {
      message.error(
        'Effective date is required for SEC basis or final reports.'
      )
      return
    }
    const projectId = projectIdFromSource ?? sourceProject?.id
    if (projectId == null) {
      message.error('Please select a source project first.')
      return
    }
    const payload = {
      project_id: projectId,
      general: {
        compliance_basis: complianceBasis,
        effective_date: effectiveDate
          ? dayjs(effectiveDate).format('YYYY-MM-DD')
          : undefined,
      },
      wells: wells.map((w) => ({ uwi: w.uwi })),
    }
    compute(payload)
  }, [
    compute,
    complianceBasis,
    effectiveDate,
    effectiveDateRequired,
    projectIdFromSource,
    sourceProject,
    wells,
  ])

  const result = response?.data
  const projectId = projectIdFromSource ?? sourceProject?.id ?? null

  // The reserves report payload reused for export (same shape as onGenerate).
  const buildReportPayload = useCallback(() => {
    if (projectId == null) return null
    return {
      project_id: projectId,
      general: {
        compliance_basis: complianceBasis,
        effective_date: effectiveDate
          ? dayjs(effectiveDate).format('YYYY-MM-DD')
          : undefined,
      },
      wells: wells.map((w) => ({ uwi: w.uwi })),
    }
  }, [projectId, complianceBasis, effectiveDate, wells])

  // --- Version list ---------------------------------------------------------
  const {
    mutate: refreshVersions,
    isPending: isLoadingVersions,
  } = useMutation({
    mutationFn: () =>
      listReservesVersions({
        projectId,
        effectiveDate: effectiveDate
          ? dayjs(effectiveDate).format('YYYY-MM-DD')
          : undefined,
      }),
    onSuccess: (res) => {
      // Defensive: BE may return a bare array or {results:[...]}.
      const payload = res?.data
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
        ? payload.results
        : []
      setVersions(list)
    },
    onError: () => setVersions([]),
  })

  // --- Save a new version ---------------------------------------------------
  const { mutate: saveVersion, isPending: isSaving } = useMutation({
    mutationFn: (payload) => saveReservesVersion(payload),
    onSuccess: (res) => {
      message.success('Reserves report version saved.')
      const saved = res?.data
      if (saved?.id != null) setSelectedVersion(saved)
      refreshVersions()
    },
  })

  const onSaveVersion = useCallback(() => {
    if (projectId == null) {
      message.error('Please select a source project first.')
      return
    }
    // effective_date required for SEC or Final (mirrors the generate gate).
    if (effectiveDateRequired && !effectiveDate) {
      message.error(
        'Effective date is required for SEC basis or final reports.'
      )
      return
    }
    saveVersion({
      projectId,
      effectiveDate: effectiveDate
        ? dayjs(effectiveDate).format('YYYY-MM-DD')
        : undefined,
      complianceBasis,
      reservesSnapshot: result ?? undefined,
      evaluatorName: evaluator.name,
      evaluatorCredentials: evaluator.qualification,
      methodStatement: evaluator.methodStatement,
      asOfNote: evaluator.asOf,
      changeNote,
    })
  }, [
    projectId,
    effectiveDateRequired,
    effectiveDate,
    complianceBasis,
    result,
    evaluator,
    changeNote,
    saveVersion,
  ])

  // --- View a version's detail ---------------------------------------------
  const { mutate: loadVersionDetail } = useMutation({
    mutationFn: (id) => getReservesVersion(id),
    onSuccess: (res) => {
      if (res?.data) setSelectedVersion(res.data)
    },
  })

  // --- State-machine transition --------------------------------------------
  const { mutate: doTransition, isPending: isTransitioning } = useMutation({
    mutationFn: ({ id, action, note }) =>
      transitionReservesVersion(id, action, note),
    onSuccess: (res, variables) => {
      message.success(
        `Version ${ACTION_LABELS[variables.action] || variables.action} succeeded.`
      )
      if (res?.data?.id != null) setSelectedVersion(res.data)
      refreshVersions()
    },
  })

  // --- Export --------------------------------------------------------------
  const { mutate: runExport, isPending: isExporting } = useMutation({
    mutationFn: (format) => {
      const payload = buildReportPayload()
      return exportReservesReport(format, payload)
    },
    onSuccess: (res) => {
      if (!res) return
      if (res.ok) {
        fileSaver.saveAs(res.blob, res.filename)
        if (res.draft) {
          message.warning(
            'ARIES export downloaded as a DRAFT — pending sample validation.'
          )
        } else {
          message.success('Export downloaded.')
        }
        return
      }
      if (res.pending) {
        // PHDwin 422 — friendly, non-modal.
        message.info(res.detail)
        return
      }
      message.error(res.detail || 'Export failed.')
    },
  })

  const onExport = useCallback(
    (format) => {
      const payload = buildReportPayload()
      if (!payload) {
        message.error('Please select a source project first.')
        return
      }
      runExport(format)
    },
    [buildReportPayload, runExport]
  )

  // --- Reconcile -----------------------------------------------------------
  const { mutate: runReconcile, isPending: isReconciling } = useMutation({
    mutationFn: ({ priorId, currentId }) =>
      reconcileReserves({
        projectId,
        priorVersionId: priorId,
        currentVersionId: currentId,
      }),
    onSuccess: (res) => setReconResult(res?.data ?? null),
    onError: () => setReconResult(null),
  })

  const onReconcile = useCallback(() => {
    if (reconPrior == null || reconCurrent == null) {
      message.error('Select both a prior and a current version to reconcile.')
      return
    }
    if (reconPrior === reconCurrent) {
      message.error('Prior and current versions must differ.')
      return
    }
    runReconcile({ priorId: reconPrior, currentId: reconCurrent })
  }, [reconPrior, reconCurrent, runReconcile])

  const wellsReserves = useMemo(
    () => result?.wells_reserves ?? [],
    [result]
  )
  const assetSummary = result?.asset_summary ?? null
  const warnings = result?.warnings ?? []

  // months_used < 12 → SEC price window incomplete. Collect the offending
  // wells for a dedicated banner (in addition to any BE-supplied warnings).
  const shortHistoryWells = useMemo(
    () =>
      wellsReserves.filter(
        (w) => !w.skipped && w.months_used != null && w.months_used < 12
      ),
    [wellsReserves]
  )
  const skippedWells = useMemo(
    () => wellsReserves.filter((w) => w.skipped),
    [wellsReserves]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Well',
        dataIndex: 'well_name',
        key: 'well_name',
        fixed: 'left',
        render: (name, row) => (
          <div className="vstack">
            <Text className="text-light">{name || row.uwi || '—'}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.uwi}
            </Text>
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) =>
          status ? (
            <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
          ) : (
            '—'
          ),
      },
      {
        title: 'Phase',
        dataIndex: 'phase',
        key: 'phase',
        render: (phase, row) => (
          <span>
            {phase || '—'}
            {row.unit ? (
              <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                ({scaledUnit(row.unit)})
              </Text>
            ) : null}
          </span>
        ),
      },
      {
        title: 'EUR P90 / P50 / P10',
        key: 'eur',
        render: (_, row) => {
          if (row.status === 'PUD' && row.pud_requires_type_curve) {
            return (
              <Tooltip title="PUD reserves require a Tier-3 type curve (RS-A pending).">
                <Text type="warning">{PUD_PLACEHOLDER}</Text>
              </Tooltip>
            )
          }
          const eur = row.eur || {}
          const u = scaledUnit(eur.unit || row.unit)
          return (
            <div className="vstack" style={{ minWidth: 160 }}>
              <Text className="text-light">
                {fmt(eur.P90)} / {fmt(eur.P50)} / {fmt(eur.P10)}{' '}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {u}
                </Text>
              </Text>
              {eur.cum_to_date != null ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  cum-to-date: {fmt(eur.cum_to_date)} {u}
                </Text>
              ) : null}
              {toBoe(eur.P50, eur.unit || row.unit) != null ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  P50 ≈ {fmt(toBoe(eur.P50, eur.unit || row.unit))} MBOE
                </Text>
              ) : null}
            </div>
          )
        },
      },
      {
        title: '1P (gross / net)',
        key: 'p1',
        render: (_, row) => {
          const b = grossNet(pickClass(row.classification, 'p1'))
          return (
            <span>
              {fmt(b.gross)} / {fmt(b.net)}
            </span>
          )
        },
      },
      {
        title: '2P (gross / net)',
        key: 'p2',
        render: (_, row) => {
          const b = grossNet(pickClass(row.classification, 'p2'))
          return (
            <span>
              {fmt(b.gross)} / {fmt(b.net)}
            </span>
          )
        },
      },
      {
        title: '3P (gross / net)',
        key: 'p3',
        render: (_, row) => {
          const b = grossNet(pickClass(row.classification, 'p3'))
          return (
            <span>
              {fmt(b.gross)} / {fmt(b.net)}
            </span>
          )
        },
      },
      {
        title: 'NRI',
        key: 'nri',
        render: (_, row) => fmt(row.classification?.nri, 4),
      },
      {
        title: 'Econ. limit (q_econ)',
        key: 'q_econ',
        render: (_, row) => {
          const el = row.economic_limit || {}
          if (el.q_econ == null) return '—'
          return (
            <div className="vstack">
              <Text className={el.uneconomic ? 'text-danger' : 'text-light'}>
                {fmt(el.q_econ)} {el.unit || ''}
              </Text>
              {el.source ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {el.source}
                  {el.uneconomic ? ' · uneconomic' : ''}
                </Text>
              ) : null}
            </div>
          )
        },
      },
      {
        title: 'Months',
        dataIndex: 'months_used',
        key: 'months_used',
        render: (m) =>
          m == null ? (
            '—'
          ) : (
            <Text className={m < 12 ? 'text-warning' : 'text-light'}>{m}</Text>
          ),
      },
    ],
    []
  )

  const summaryRows = useMemo(() => {
    if (!assetSummary) return []
    // Defensive: support either reserves_1P/2P/3P or proved/probable/possible.
    return [
      { key: '1P', label: '1P (Proved)', bucket: pickClass(assetSummary, 'p1') },
      {
        key: '2P',
        label: '2P (Proved + Probable)',
        bucket: pickClass(assetSummary, 'p2'),
      },
      {
        key: '3P',
        label: '3P (Proved + Probable + Possible)',
        bucket: pickClass(assetSummary, 'p3'),
      },
    ]
  }, [assetSummary])

  if (!sourceProject) {
    return (
      <FormSection>
        <Text className="text-light">
          Select a source project to build a reserves report.
        </Text>
      </FormSection>
    )
  }

  return (
    <div className="vstack gap-2">
      <FormSection title="Reserves Report Settings">
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <div className="vstack gap-1">
              <Text className="text-light">Compliance Basis</Text>
              <Radio.Group
                value={complianceBasis}
                onChange={(e) => {
                  setComplianceBasis(e.target.value)
                  resetResult()
                }}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="PRMS">PRMS</Radio.Button>
                <Radio.Button value="SEC">SEC</Radio.Button>
              </Radio.Group>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="vstack gap-1">
              <Text className="text-light">
                Effective Date{effectiveDateRequired ? ' *' : ''}
              </Text>
              <DatePicker
                className="w-100"
                value={effectiveDate}
                onChange={setEffectiveDate}
                status={
                  effectiveDateRequired && !effectiveDate ? 'error' : undefined
                }
              />
              {effectiveDateRequired && !effectiveDate ? (
                <Text type="danger" style={{ fontSize: 11 }}>
                  Required for SEC basis or final reports.
                </Text>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="vstack gap-1">
              <Text className="text-light">Report Stage</Text>
              <Radio.Group
                value={markFinal ? 'final' : 'draft'}
                onChange={(e) => setMarkFinal(e.target.value === 'final')}
                optionType="button"
              >
                <Radio.Button value="draft">Draft</Radio.Button>
                <Radio.Button value="final">Final</Radio.Button>
              </Radio.Group>
            </div>
          </div>
        </div>
        <div className="hstack gap-2 mt-3">
          <Button type="primary" loading={isPending} onClick={onGenerate}>
            Generate Reserves Report
          </Button>
          {result ? (
            <Text type="secondary">
              Basis: <strong>{result.compliance_basis || complianceBasis}</strong>
              {result.effective_date ? (
                <> · As of {result.effective_date}</>
              ) : null}
            </Text>
          ) : null}
        </div>
      </FormSection>

      {warnings.length ? (
        <Alert
          type="warning"
          showIcon
          message="Reserves warnings"
          description={
            <ul className="mb-0">
              {warnings.map((w, i) => (
                <li key={i}>{typeof w === 'string' ? w : JSON.stringify(w)}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      {shortHistoryWells.length ? (
        <Alert
          type="warning"
          showIcon
          message="SEC price window incomplete"
          description={`${shortHistoryWells.length} well(s) have fewer than 12 months of history; SEC 12-month average pricing may be incomplete.`}
        />
      ) : null}

      {skippedWells.length ? (
        <Alert
          type="info"
          showIcon
          message={`${skippedWells.length} well(s) skipped`}
          description={
            <ul className="mb-0">
              {skippedWells.map((w) => (
                <li key={w.uwi}>
                  {w.well_name || w.uwi}: {w.reason || 'no reason provided'}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      {result ? (
        <FormSection title="Per-Well Reserves">
          {wellsReserves.length ? (
            <Table
              size="small"
              rowKey={(row) => row.uwi}
              columns={columns}
              dataSource={wellsReserves}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Text className="text-light">
              No reserves rows returned for this project.
            </Text>
          )}
        </FormSection>
      ) : null}

      {assetSummary ? (
        <FormSection title="Asset Summary">
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            columns={[
              { title: 'Category', dataIndex: 'label', key: 'label' },
              {
                title: 'Gross',
                key: 'gross',
                render: (_, r) => fmt(r.bucket?.gross),
              },
              {
                title: 'Net',
                key: 'net',
                render: (_, r) => fmt(r.bucket?.net),
              },
            ]}
            dataSource={summaryRows}
          />
          <div className="hstack flex-wrap gap-3 mt-2">
            {assetSummary.aggregation_method ? (
              <Text type="secondary">
                Aggregation: <strong>{assetSummary.aggregation_method}</strong>
              </Text>
            ) : null}
            {assetSummary.n_skipped != null ? (
              <Text type="secondary">Skipped: {assetSummary.n_skipped}</Text>
            ) : null}
            {assetSummary.n_pud_excluded != null ? (
              <Text type="secondary">
                PUD excluded: {assetSummary.n_pud_excluded}
              </Text>
            ) : null}
            {assetSummary.counts_by_status
              ? Object.entries(assetSummary.counts_by_status).map(
                  ([status, count]) => (
                    <Tag key={status} color={STATUS_COLORS[status] || 'default'}>
                      {status}: {count}
                    </Tag>
                  )
                )
              : null}
          </div>
          {/* Defensive: surface any additional scalar summary fields RS-A3 adds. */}
          {Object.entries(assetSummary)
            .filter(
              ([k, v]) =>
                ![
                  'reserves_1P',
                  'reserves_2P',
                  'reserves_3P',
                  'proved',
                  'probable',
                  'possible',
                  'counts_by_status',
                  'n_skipped',
                  'n_pud_excluded',
                  'aggregation_method',
                ].includes(k) &&
                (typeof v === 'string' || typeof v === 'number')
            )
            .map(([k, v]) => (
              <Text key={k} type="secondary" className="d-block mt-1">
                {k}: {String(v)}
              </Text>
            ))}
        </FormSection>
      ) : null}

      <FormSection title="Evaluator Certification">
        <Paragraph type="secondary" className="mb-2">
          For the report cover page. Saved with the reserves report version.
        </Paragraph>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <Text className="text-light">Evaluator Name</Text>
            <Input
              value={evaluator.name}
              onChange={(e) =>
                setEvaluator((s) => ({ ...s, name: e.target.value }))
              }
              placeholder="e.g. Jane Doe"
            />
          </div>
          <div className="col-12 col-md-6">
            <Text className="text-light">Qualification</Text>
            <Input
              value={evaluator.qualification}
              onChange={(e) =>
                setEvaluator((s) => ({ ...s, qualification: e.target.value }))
              }
              placeholder="e.g. P.E. / Qualified Reserves Evaluator"
            />
          </div>
          <div className="col-12 col-md-6">
            <Text className="text-light">As-Of Date</Text>
            <Input
              value={evaluator.asOf}
              onChange={(e) =>
                setEvaluator((s) => ({ ...s, asOf: e.target.value }))
              }
              placeholder="YYYY-MM-DD"
            />
          </div>
          <div className="col-12">
            <Text className="text-light">Method Statement</Text>
            <Input.TextArea
              rows={3}
              value={evaluator.methodStatement}
              onChange={(e) =>
                setEvaluator((s) => ({
                  ...s,
                  methodStatement: e.target.value,
                }))
              }
              placeholder="Declaration of methods, standards, and assumptions used in this evaluation."
            />
          </div>
        </div>
        <div className="row g-3 mt-1">
          <div className="col-12">
            <Text className="text-light">Change Note (for this version)</Text>
            <Input.TextArea
              rows={2}
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="What changed since the prior version (revisions, new wells, price deck, ...)."
            />
          </div>
        </div>
        <Divider className="my-2" />
        <Title level={5} className="text-light">
          Declaration
        </Title>
        <Paragraph type="secondary" className="mb-2">
          Reserves classified on a{' '}
          <strong>{complianceBasis}</strong> basis
          {effectiveDate ? (
            <> as of <strong>{dayjs(effectiveDate).format('YYYY-MM-DD')}</strong></>
          ) : null}
          . BOE aggregated at 6 Mscf/bbl. P90 &lt; P50 &lt; P10 (increasing
          confidence).
        </Paragraph>
        <div className="hstack gap-2">
          <Tooltip
            title={
              !result
                ? 'Generate the reserves report first to capture a snapshot.'
                : ''
            }
          >
            <Button
              type="primary"
              loading={isSaving}
              onClick={onSaveVersion}
              disabled={projectId == null}
            >
              Save Reserves Report Version
            </Button>
          </Tooltip>
          {!result ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No computed snapshot yet — the version will be saved without
              reserves numbers.
            </Text>
          ) : null}
        </div>
      </FormSection>

      {/* --- WS6 RS-D: version bar ------------------------------------- */}
      <FormSection title="Report Versions">
        <div className="hstack gap-2 mb-2">
          <Button
            size="small"
            loading={isLoadingVersions}
            onClick={() => refreshVersions()}
            disabled={projectId == null}
          >
            Load Versions
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {versions.length
              ? `${versions.length} version(s) for this project`
              : 'No versions loaded.'}
          </Text>
        </div>
        {versions.length ? (
          <Table
            size="small"
            rowKey={(row) => row.id}
            pagination={false}
            dataSource={versions}
            onRow={(row) => ({
              onClick: () => loadVersionDetail(row.id),
              style: {
                cursor: 'pointer',
                background:
                  selectedVersion?.id === row.id
                    ? 'rgba(255,255,255,0.06)'
                    : undefined,
              },
            })}
            columns={[
              {
                title: '#',
                key: 'version_no',
                render: (_, r) =>
                  firstDefined(r, ['version_no', 'version', 'number']) ?? '—',
              },
              {
                title: 'Effective Date',
                key: 'effective_date',
                render: (_, r) =>
                  firstDefined(r, ['effective_date', 'as_of_date']) ?? '—',
              },
              {
                title: 'Basis',
                key: 'compliance_basis',
                render: (_, r) =>
                  firstDefined(r, ['compliance_basis', 'basis']) ?? '—',
              },
              {
                title: 'Status',
                key: 'status',
                render: (_, r) => {
                  const s = normStatus(r.status)
                  return r.status ? (
                    <Tag color={VERSION_STATUS_COLORS[s] || 'default'}>
                      {String(r.status).toUpperCase()}
                    </Tag>
                  ) : (
                    '—'
                  )
                },
              },
              {
                title: 'Evaluator',
                key: 'evaluator',
                render: (_, r) =>
                  firstDefined(r, ['evaluator_name', 'evaluator']) ?? '—',
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, r) => {
                  const acts = actionsForStatus(r.status)
                  if (!acts.length) {
                    return isLocked(r) ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        locked (read-only)
                      </Text>
                    ) : (
                      '—'
                    )
                  }
                  return (
                    <Space
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {acts.map((action) => (
                        <Button
                          key={action}
                          size="small"
                          type={ACTION_BUTTON_TYPE[action] || 'default'}
                          danger={action === 'reject'}
                          loading={isTransitioning}
                          onClick={() => doTransition({ id: r.id, action })}
                        >
                          {ACTION_LABELS[action]}
                        </Button>
                      ))}
                    </Space>
                  )
                },
              },
            ]}
          />
        ) : null}

        {selectedVersion ? (
          <div className="mt-3">
            <Divider className="my-2" />
            <Title level={5} className="text-light">
              Version Detail
              {isLocked(selectedVersion) ? (
                <Tag color="gold" className="ms-2">
                  LOCKED · read-only
                </Tag>
              ) : null}
            </Title>
            <div className="row g-2">
              {[
                ['Version #', firstDefined(selectedVersion, ['version_no', 'version', 'number'])],
                ['Status', selectedVersion.status],
                ['Effective Date', firstDefined(selectedVersion, ['effective_date', 'as_of_date'])],
                ['Basis', firstDefined(selectedVersion, ['compliance_basis', 'basis'])],
                ['Evaluator', firstDefined(selectedVersion, ['evaluator_name', 'evaluator'])],
                ['Credentials', firstDefined(selectedVersion, ['evaluator_credentials', 'credentials'])],
                ['As-Of Note', firstDefined(selectedVersion, ['as_of_note'])],
                ['Change Note', firstDefined(selectedVersion, ['change_note'])],
              ].map(([label, value]) => (
                <div className="col-12 col-md-6" key={label}>
                  <Text type="secondary">{label}: </Text>
                  <Text className="text-light">{value ?? '—'}</Text>
                </div>
              ))}
              {firstDefined(selectedVersion, ['method_statement']) ? (
                <div className="col-12">
                  <Text type="secondary">Method Statement: </Text>
                  <Text className="text-light">
                    {selectedVersion.method_statement}
                  </Text>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </FormSection>

      {/* --- WS6 RS-C2: export button group --------------------------- */}
      <FormSection title="Export">
        <Paragraph type="secondary" className="mb-2">
          Exports use the current report settings (basis / effective date /
          wells).
        </Paragraph>
        <Space wrap>
          <Button
            type="primary"
            loading={isExporting}
            onClick={() => onExport('csv')}
            disabled={projectId == null}
          >
            CSV
          </Button>
          <Button
            type="primary"
            loading={isExporting}
            onClick={() => onExport('xlsx')}
            disabled={projectId == null}
          >
            Excel
          </Button>
          <Button
            loading={isExporting}
            onClick={() => onExport('combocurve')}
            disabled={projectId == null}
          >
            ComboCurve
          </Button>
          <Tooltip title="ARIES export is a draft pending sample validation.">
            <Button
              loading={isExporting}
              onClick={() => onExport('aries')}
              disabled={projectId == null}
              style={{
                borderColor: '#d4b106',
                color: '#d4b106',
              }}
            >
              ARIES
              <Tag color="gold" className="ms-2" style={{ marginRight: 0 }}>
                Draft
              </Tag>
            </Button>
          </Tooltip>
          <Tooltip title="Requires validated real .phd samples — not yet available.">
            {/* span wrapper so the tooltip works on a disabled button */}
            <span>
              <Button disabled>PHDwin</Button>
            </span>
          </Tooltip>
        </Space>
      </FormSection>

      {/* --- WS6 RS-C1: reconciliation view --------------------------- */}
      <FormSection title="Reserves Reconciliation">
        <Paragraph type="secondary" className="mb-2">
          Beginning → movements → ending, by category. Load versions above to
          populate the selectors.
        </Paragraph>
        <div className="row g-3 align-items-end">
          <div className="col-12 col-md-4">
            <div className="vstack gap-1">
              <Text className="text-light">Prior Version</Text>
              <Select
                className="w-100"
                value={reconPrior}
                onChange={setReconPrior}
                placeholder="Select prior"
                options={versions.map((v) => ({
                  value: v.id,
                  label: `#${
                    firstDefined(v, ['version_no', 'version', 'number']) ?? v.id
                  } · ${firstDefined(v, ['effective_date', 'as_of_date']) ?? '—'}`,
                }))}
                disabled={!versions.length}
              />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="vstack gap-1">
              <Text className="text-light">Current Version</Text>
              <Select
                className="w-100"
                value={reconCurrent}
                onChange={setReconCurrent}
                placeholder="Select current"
                options={versions.map((v) => ({
                  value: v.id,
                  label: `#${
                    firstDefined(v, ['version_no', 'version', 'number']) ?? v.id
                  } · ${firstDefined(v, ['effective_date', 'as_of_date']) ?? '—'}`,
                }))}
                disabled={!versions.length}
              />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <Button
              type="primary"
              loading={isReconciling}
              onClick={onReconcile}
              disabled={reconPrior == null || reconCurrent == null}
            >
              Reconcile
            </Button>
          </div>
        </div>

        {isReconciling ? (
          <div className="mt-3">
            <Spin />
          </div>
        ) : null}

        {reconResult ? (
          <ReconciliationTable
            recon={reconResult}
            complianceBasis={
              reconResult.compliance_basis || complianceBasis
            }
          />
        ) : null}
      </FormSection>
    </div>
  )
}

// Reconciliation table — defensive across RS-C1 field renames. Renders each
// requested category (SEC → 1P only) as a row of movement buckets, flags the
// residual in red when the beginning + movements don't tie to ending.
const ReconciliationTable = ({ recon, complianceBasis }) => {
  const categories = recon?.categories || {}
  // SEC discloses proved (1P) only; PRMS shows 1P/2P/3P.
  const wanted =
    String(complianceBasis).toUpperCase() === 'SEC'
      ? ['1P']
      : ['1P', '2P', '3P']
  const present = wanted.filter((c) => categories[c] != null)

  if (!present.length) {
    return (
      <Alert
        className="mt-3"
        type="info"
        showIcon
        message="No reconciliation categories returned."
      />
    )
  }

  return (
    <div className="mt-3 vstack gap-2">
      <div className="hstack flex-wrap gap-3">
        {recon.prior_effective_date ? (
          <Text type="secondary">
            Prior: <strong>{recon.prior_effective_date}</strong>
          </Text>
        ) : null}
        {recon.current_effective_date ? (
          <Text type="secondary">
            Current: <strong>{recon.current_effective_date}</strong>
          </Text>
        ) : null}
        {recon.compliance_basis ? (
          <Text type="secondary">
            Basis: <strong>{recon.compliance_basis}</strong>
          </Text>
        ) : null}
      </div>

      {Array.isArray(recon.warnings) && recon.warnings.length ? (
        <Alert
          type="warning"
          showIcon
          message="Reconciliation warnings"
          description={
            <ul className="mb-0">
              {recon.warnings.map((w, i) => (
                <li key={i}>
                  {typeof w === 'string' ? w : JSON.stringify(w)}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Table
        size="small"
        rowKey="key"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={present.map((cat) => ({ key: cat, cat, data: categories[cat] }))}
        columns={[
          {
            title: 'Category',
            key: 'cat',
            fixed: 'left',
            render: (_, r) => {
              const unit = r.data?.unit
              return (
                <span>
                  <strong>{r.cat}</strong>
                  {unit ? (
                    <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                      ({unit})
                    </Text>
                  ) : null}
                </span>
              )
            },
          },
          ...RECON_BUCKETS.map((b) => ({
            title: b.label,
            key: b.key,
            render: (_, r) => fmt(reconValue(r.data, b.aliases)),
          })),
          {
            title: 'Residual',
            key: 'residual',
            render: (_, r) => {
              const residual = reconValue(r.data, ['residual'])
              if (residual == null) return '—'
              const n = Number(residual)
              const closes = !Number.isNaN(n) && Math.abs(n) < 1e-6
              return (
                <Text className={closes ? 'text-light' : 'text-danger'}>
                  {fmt(residual)}
                  {!closes ? ' ⚠' : ''}
                </Text>
              )
            },
          },
        ]}
      />
    </div>
  )
}

export default ReservesReport
